import { useGlobalContainerRef } from '@/wc/helper/ref';
import { IconHelp, Tooltip } from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import {
  CSSProperties,
  FC,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './TableTextOverflow.module.scss';

const cx = classNames.bind(styles);

export type Props = {
  title: string;
  tooltipMessage?: string;
  isLastColumn?: boolean;
  isTextAlignEnd?: boolean;
  /**
   * NA-5596: opt-in pass-through to antd's own `Tooltip` overlay style
   * prop, left `undefined` (unchanged existing styling, relied on by every
   * other consumer of this shared component) unless a consumer opts in.
   */
  overlayStyle?: CSSProperties;
};

export const TableTextOverflow: FC<Props> = memo(
  ({
    title,
    tooltipMessage,
    isLastColumn = false,
    isTextAlignEnd = false,
    overlayStyle,
  }) => {
    const titleRef = useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);
    const globalContainer = useGlobalContainerRef();

    useEffect(() => {
      const checkTruncation = () => {
        const element = titleRef.current;
        if (element) {
          setIsTruncated(element.scrollHeight > element.clientHeight);
        }
      };

      checkTruncation();

      const resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(checkTruncation);
      });

      if (titleRef.current) {
        resizeObserver.observe(titleRef.current);
      }

      return () => {
        resizeObserver.disconnect();
      };
    }, [title]);

    const tooltipAlign = useMemo(() => {
      return {
        overflow: { adjustX: true },
      };
    }, []);

    const tooltipArrow = useMemo(() => {
      return { pointAtCenter: true };
    }, []);

    return (
      <div
        className={cx(styles['title-container'], {
          [styles['text-align-end']]: isTextAlignEnd,
        })}
      >
        <span ref={titleRef} className={cx(styles['title-text'])}>
          <div className={cx(styles['tooltip-container'])}>
            {isTruncated ? (
              <Tooltip
                delayMs={500}
                title={title}
                placement={isLastColumn ? 'topRight' : 'top'}
                getPopupContainer={globalContainer}
                overlayStyle={overlayStyle}
              >
                {title}
              </Tooltip>
            ) : (
              title
            )}
          </div>
        </span>
        {tooltipMessage ? (
          <div className={cx(styles['tooltip-container'])}>
            <Tooltip
              title={tooltipMessage}
              placement={isLastColumn ? 'topRight' : 'top'}
              align={tooltipAlign}
              arrow={tooltipArrow}
              getPopupContainer={globalContainer}
              delayMs={500}
            >
              <div className={cx(styles['tooltip-icon-container'])}>
                <IconHelp size={14} />
              </div>
            </Tooltip>
          </div>
        ) : null}
      </div>
    );
  }
);
TableTextOverflow.displayName = 'TableTextOverflow';
